import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { anonymousNickname } from '@/lib/nickname';
import { laravelOwnsData } from '@/lib/dataOwner';
import { esitoScritturaNickname, type EsitoNickname } from '@/lib/nicknameEsito';
import { useUsersMeQuery, useUpdateUserMutation } from '@/services/endpoints/users';
import { useQlashUser } from '@/hooks/useQlashUser';
import { useSalvaNickname } from '@/hooks/useSalvaNickname';
import { useTelegram } from '@/hooks/useTelegram';
import { useMainButton } from '@/hooks/useMainButton';
import { useHaptics } from '@/hooks/useHaptics';

interface WelcomeStepProps {
  onNext: () => void;
}

/**
 * Welcome + nickname. Prefills with the Telegram first name (PUT /users/:id with the
 * user's current record spread back so the Laravel endpoint gets its full body). Skip
 * keeps an existing real nickname; if the current one is empty or email-derived (the
 * reconciler's auto-nickname leaks the email prefix) it writes `anonymous#NNNNNN`.
 *
 * 🔴 **«Best-effort» non voleva dire «in silenzio», e per mesi ha voluto dire quello.**
 * Il `catch` era vuoto e il wizard proseguiva ⇒ chi sceglieva un nickname **gia' preso
 * credeva di averlo**. Ora l'errore si vede e ha un nome (`lib/nicknameEsito.ts`), e
 * l'avanzamento automatico resta **solo su «Salta»**, dove non c'e' nessuna convinzione
 * da proteggere. (Difetto misurato da `limulo-sezioni-orc` su `bf6910f`, instradato qui
 * come proprietario dell'onboarding.)
 *
 * 🔴 **Sulla build Limulo questo step scriveva sul monolite, e il monolite non c'era.**
 * [M `tma-orc-5-nickname`, stg-tma, 2026-09-19 20:43-20:44Z] `GET /users/me` → 503 dopo 9 s,
 * `me` assente ⇒ il ramo di scrittura saltato e `onNext()` chiamato: il wizard avanzava con
 * **zero scritture** e `GET /v1/me` restava `nickname: null`. ⇒ su Limulo la scrittura passa
 * da `useSalvaNickname` (`PUT /v1/me/nickname`), la stessa delle Impostazioni, e il monolite
 * non si interroga. Niente `anonymousNickname()` li': il `#` e' fuori formato (400), e
 * ADR-0125 rifiuta un nome che «non dimostra scelta dell'utente». Il ramo Laravel e' quello
 * di prima, invariato.
 */
export function WelcomeStep({ onNext }: WelcomeStepProps) {
  const { t, i18n } = useTranslation('onboarding');
  const { user: tgUser } = useTelegram();
  const suLaravel = laravelOwnsData();
  const { data: me, isLoading: meLoading } = useUsersMeQuery(undefined, { skip: !suLaravel });
  const [updateUser, { isLoading: laravelSaving }] = useUpdateUserMutation();
  const qlashUser = useQlashUser();
  const { salva, inCorso: limuloSaving } = useSalvaNickname();
  const saving = laravelSaving || limuloSaving;
  const haptics = useHaptics();
  const [nickname, setNickname] = useState('');
  const [touched, setTouched] = useState(false);
  // 🔴 `null` = nessun errore. Prima questo stato non esisteva e il `catch` era vuoto:
  // il wizard proseguiva, e chi aveva scelto un nickname gia' preso **credeva di
  // averlo**. Il segnale positivo (`haptics.success`) stava solo nel ramo riuscito ⇒
  // in caso di errore l'utente non riceveva NIENTE: un fallimento indistinguibile da
  // un successo silenzioso.
  const [esito, setEsito] = useState<EsitoNickname | null>(null);

  const emailPrefix = (me?.email ?? '').split('@')[0];
  // Su Limulo il nome scelto e' `username` solo se il server dice che la scelta c'e'
  // (`identity.ts`): il segnaposto «Player» non e' un nickname da proporre.
  const nicknameAttuale = suLaravel
    ? me?.nickname
    : qlashUser?.requiresNickname === false
      ? qlashUser.username
      : undefined;
  const hasRealNickname = !!nicknameAttuale && nicknameAttuale !== emailPrefix;
  const prefill = hasRealNickname ? (nicknameAttuale ?? '') : (tgUser?.first_name ?? '');
  const shown = touched ? nickname : prefill;

  /**
   * `avanzaComunque` separa i due gesti, e la distinzione non e' cosmetica:
   * · **«Continua»** = *«ho scelto QUESTO nome»* ⇒ se la scrittura fallisce, l'utente
   *   resta dov'e' e legge perche': proseguire gli lascerebbe una **convinzione falsa**.
   * · **«Salta»** = *«del nickname non mi importa»* ⇒ nessuna convinzione da proteggere,
   *   e fermarlo lo intrappolerebbe su un passo che stava rinunciando a fare.
   * ⇒ Il vecchio commento («never block the wizard on a profile write») resta vero **dove
   * serviva davvero**: sull'uscita. Non era vero come regola per entrambi.
   */
  const save = async (finalNick: string, avanzaComunque: boolean) => {
    if (!suLaravel) {
      if (finalNick && finalNick !== nicknameAttuale) {
        setEsito(null);
        const err = await salva(finalNick);
        if (err) {
          setEsito(err);
          haptics.notification('error');
          if (!avanzaComunque) return;
        } else {
          haptics.notification('success');
        }
      }
      onNext();
      return;
    }
    if (me && finalNick && finalNick !== me.nickname) {
      setEsito(null);
      try {
        await updateUser({
          id: me.id,
          nickname: finalNick,
          country: me.country ?? '',
          birthday: me.birthday ?? '',
          name: me.name ?? '',
          accepted_privacy: me.accepted_privacy ?? true,
          locale: me.locale ?? i18n.language,
        }).unwrap();
        haptics.notification('success');
      } catch (err) {
        setEsito(esitoScritturaNickname(err));
        haptics.notification('error');
        if (!avanzaComunque) return;
      }
    }
    onNext();
  };

  // ⏳ `anonymousNickname()` HA UNA SCADENZA, e chi la estendesse la estenderebbe nella
  // direzione sbagliata: quando il nickname passera' a Limulo (ADR-0125 `proposed`) il
  // formato e' `[a-z0-9_]` 3-32, e il **`#`** di `anonymous#NNNNNN` e' **fuori formato**
  // ⇒ `400`. L'ADR rifiuta anche il backfill automatico per principio — *«non dimostra
  // scelta dell'utente»*. ⇒ **da quel percorso va RITIRATA, non adattata**, ed e' lavoro
  // del piano di migrazione (`limulo-sezioni-orc`), non di questo file.
  // Dettaglio: `reports/2026-09-10-profilo-nickname-caso-null-disegno.md` §5.7.
  const submit = () => void save(shown.trim() || (suLaravel ? anonymousNickname() : ''), false);
  // Su Limulo «Salta» non scrive: nessun nome inventato a nome dell'utente.
  const skip = () => void save(hasRealNickname || !suLaravel ? '' : anonymousNickname(), true);

  useMainButton({
    text: t('welcome.cta'),
    // Su Limulo un campo vuoto non e' una scelta: «Continua» si spegne (resta «Salta»).
    enabled: !saving && !meLoading && (suLaravel || shown.trim() !== ''),
    loading: saving || meLoading,
    onClick: submit,
  });

  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
      <h1 className="text-3xl font-bold text-brand">{t('welcome.title')}</h1>
      <p className="text-sm text-fg-muted">{t('welcome.body')}</p>
      <label className="flex w-full flex-col gap-2 text-left">
        <span className="text-sm font-semibold text-fg">{t('welcome.nicknameLabel')}</span>
        <input
          type="text"
          value={shown}
          maxLength={32}
          onChange={(e) => {
            setTouched(true);
            setNickname(e.target.value);
          }}
          className="rounded-xl border border-border bg-bg-secondary p-3 text-sm text-fg outline-none focus:border-brand"
        />
        {/* Come nelle Impostazioni: la regola si mostra solo dove il server la applica. */}
        {!suLaravel && (
          <span className="text-xs text-fg-muted">
            {t('nickname.rule', {
              defaultValue: '3–32 characters: lowercase letters, numbers and underscore.',
            })}
          </span>
        )}
      </label>
      {esito && (
        <p className="text-xs text-danger" role="alert">
          {esito === 'gia-preso'
            ? t('welcome.nicknameTaken', {
                defaultValue: 'Questo nickname è già di qualcun altro. Scegline un altro.',
              })
            : esito === 'formato'
              ? suLaravel
                ? t('welcome.nicknameInvalid', {
                    defaultValue: 'Questo nickname non è valido. Prova con lettere e numeri.',
                  })
                : t('nickname.badFormat', {
                    defaultValue: 'Questo nickname non è nel formato richiesto.',
                  })
              : t('welcome.nicknameFailed', {
                  defaultValue: 'Non è stato possibile salvare il nickname. Riprova.',
                })}
        </p>
      )}
      <button
        type="button"
        onClick={skip}
        disabled={saving || meLoading}
        className="text-xs font-semibold text-fg-muted underline"
      >
        {t('welcome.skip')}
      </button>
    </div>
  );
}
